import type { Translations } from './ru'

export const enTranslations: Translations = {
  langSwitcher: {
    aria: 'Switch language',
    ru: 'Русский',
    en: 'English',
  },

  nav: {
    allTools: 'All tools',
    blog: 'Blog',
  },

  badge: {
    local: 'In browser',
    server: 'On server',
    localFirst: 'In browser*',
    localTitle: 'Text is processed in your browser and never sent to a server',
    serverTitle: 'Text is sent to a server for processing',
    localFirstTitle:
      'Processed in your browser by default. A server option is available when needed.',
  },

  common: {
    textTools: 'Tools',
    scenarios: 'Use cases',
    howTo: 'How to use it',
    faq: 'Questions',
    otherTools: 'Other tools',
    allTools: 'All tools',
    updated: 'Updated',
    allBlogPosts: 'All blog posts',
  },

  hero: {
    title: 'Online text tools',
    description:
      'Count characters, remove duplicates, clean and convert text right in your browser. No sign-up.',
  },

  catalog: {
    title: 'Tool catalog',
  },

  categories: {
    count: 'Count',
    clean: 'Clean',
    format: 'Format',
    generate: 'Generate',
    seo: 'SEO and social',
  },

  tools: {
    textCounter: {
      title: 'Character counter',
      shortDescription: 'Characters, words, lines, paragraphs and UTF-8 text size.',
      longDescription:
        'A fast counter for drafts, SEO copy, interface strings and posts with limits. Text is processed locally in your browser.',
      seoTitle: 'Character counter online: count characters and words — txtify.ru',
      seoDescription:
        'Count characters, words, lines, paragraphs and UTF-8 text size. Free, no sign-up and no server upload.',
      useCases: ['SEO title and description', 'Posts with limits', 'UI strings', 'UTF-8 size'],
      howTo: [
        'Paste text into the editor.',
        'Watch the metrics update while you type.',
        'Copy the text back or clear the field.',
      ],
      faq: [
        {
          question: 'Is the text sent to a server?',
          answer: 'No. The counter runs in the browser, so the text stays on your device.',
        },
        {
          question: 'Are emoji counted correctly?',
          answer:
            'Yes. If the browser supports Intl.Segmenter, combined symbols are counted as visible graphemes.',
        },
      ],
    },
    textCase: {
      title: 'Text case',
      shortDescription: 'UPPERCASE, lowercase, Title Case and sentence case.',
      longDescription:
        'Quickly normalize headings, lists and imported strings to the case you need.',
      seoTitle: 'Change text case online — txtify.ru',
      seoDescription: 'Convert text to upper, lower, title or sentence case.',
      useCases: ['Headings', 'Imported lists', 'Copy normalization'],
      modeLowerHint: 'all lowercase',
      modeUpperHint: 'all uppercase',
      modeTitleHint: 'each word capitalised',
      modeSentenceHint: 'first word capitalised',
      modeCapitalizeHint: 'only first letter',
      howTo: [
        'Paste or type text into the left panel.',
        'Select the transformation mode: lowercase, UPPERCASE, Title Case, Sentence case or Capitalize.',
        'Copy the result or swap input and output using the "Swap" button.',
      ],
      faq: [
        {
          question: 'Is the text sent to a server?',
          answer: 'No. Case conversion happens locally in your browser. Text never leaves your device.',
        },
        {
          question: 'Does it work with non-English text?',
          answer: 'Yes. All modes handle non-English text correctly, including accented characters and combined symbols.',
        },
        {
          question: 'What is the difference between Title Case and Capitalize?',
          answer: 'Title Case capitalises the first letter of every word. Capitalize only capitalises the first letter of the entire text and leaves the rest unchanged.',
        },
      ],
    },
    dedupeLines: {
      title: 'Remove duplicate keywords, phrases and lines',
      shortDescription: 'Keep one occurrence of each keyword, phrase or line.',
      longDescription:
        'Remove duplicate keywords, phrases and lines with clear comparison rules. The first occurrence and source order are preserved.',
      seoTitle: 'Remove duplicate keywords and phrases online — txtify.ru',
      seoDescription:
        'Remove duplicate keywords and phrases online: clear duplicate lines from a list, with case and whitespace rules. Order is kept and text stays in your browser.',
      useCases: ['Keyword lists', 'Repeated phrases', 'Email exports', 'Lists and checklists'],
      howTo: [
        'Put each item, line or phrase on its own line and paste the text into the left editor.',
        'If needed, ignore case or outer Unicode whitespace when comparing and remove blank lines.',
        'Check the statistics: the first occurrence of every item stays in its original position.',
        'Copy the result or download it as a .txt file.',
      ],
      faq: [
        {
          question: 'Is the text sent to a server?',
          answer: 'No. The tool runs entirely in your browser — no data leaves your device.',
        },
        {
          question: 'What counts as a duplicate?',
          answer:
            'Matches are exact by default: case and outer whitespace matter. You can explicitly ignore either difference in the comparison settings.',
        },
        {
          question: 'How are list items and phrases separated?',
          answer:
            'Each item or phrase must occupy one line. A comma inside a line remains part of the text and is never treated as a separator.',
        },
        {
          question: 'Will line order or spelling change?',
          answer:
            'No. The first occurrence always stays in its original position and keeps its exact case and spacing.',
        },
        {
          question: 'What happens to blank lines?',
          answer:
            'They take part in comparison by default: the first blank stays and later blanks are duplicates. The separate option removes lines made only of Unicode whitespace and reports them outside the duplicate count.',
        },
      ],
      page: {
        sourceText: 'Source text',
        resultText: 'Result',
        ignoreCase: 'Ignore case',
        ignoreCaseDesc: 'Upper- and lowercase letters count as equal',
        ignoreOuterSpaces: 'Ignore edge spaces',
        ignoreOuterSpacesDesc: 'Edge spaces do not affect comparison and stay in the result',
        removeBlankLines: 'Remove blank lines',
        removeBlankLinesDesc: 'Remove empty lines and lines of spaces only',
        clear: 'Clear',
        copy: 'Copy',
        copied: 'Copied',
        download: 'Download .txt',
        inputPlaceholder: 'Paste keywords or phrases, one per line...',
        outputPlaceholder: 'The result will appear here...',
        inputAriaLabel: 'Source text for removing duplicate lines',
        outputAriaLabel: 'Result of duplicate removal',
        optionsTitle: 'Processing options',
        statsTitle: 'Statistics',
        statTotal: 'Total lines',
        statUnique: 'Unique lines kept',
        statRemoved: 'Duplicates removed',
        statBlankRemoved: 'Blank lines removed',
        linesUnit: 'lines',
        duplicatesUnit: 'duplicates',
        itemRule: 'One list item or phrase goes on each line. Commas inside a line remain part of the text.',
        orderRule: 'The first occurrence always stays in its original position and keeps its original spelling.',
        howTitle: 'How it works',
        howText: 'The tool compares items line by line and keeps the first occurrence of each one. Case and outer Unicode whitespace matter by default. Options change only the comparison key, so the preserved line is never rewritten. Blank-line removal is controlled independently.',
        whenTitle: 'When it is useful',
        whenText: 'Cleaning keyword lists, repeated phrases, email addresses, lists and checklists where each item occupies its own line.',
      },
    },
    textCleanup: {
      title: 'Clean text',
      shortDescription: 'Remove extra spaces, blank lines and wraps inside paragraphs.',
      longDescription:
        'Safe local cleanup for text copied from PDFs, OCR and email, with a change preview.',
      seoTitle: 'Remove extra spaces and line breaks online — txtify.ru',
      seoDescription:
        'Clean extra spaces, tabs, blank lines and wraps inside paragraphs. Runs locally in your browser with a before-and-after preview.',
      useCases: ['Text from PDFs and OCR', 'Email', 'Drafts', 'Copied paragraphs'],
      howTo: [
        'Paste text and select only the cleanup rules you need.',
        'Compare the source and result in the before-and-after preview.',
        'Check the counts and copy the cleaned text.',
      ],
      faq: [
        {
          question: 'Is text sent to a server?',
          answer:
            'No. Cleanup and preview run locally in your browser; text is neither sent nor stored.',
        },
        {
          question: 'Will separate paragraphs be joined?',
          answer:
            'No. Wrapped-line joining only combines non-empty lines inside a paragraph. A blank line remains a boundary; when blank lines are removed, neighbouring paragraphs remain separate lines.',
        },
        {
          question: 'Which characters are changed?',
          answer:
            'The tool does not normalize Unicode or alter punctuation. Non-breaking spaces stay intact. Only explicitly selected ASCII spaces, tabs and line boundaries change.',
        },
      ],
      page: {
        presetsTitle: 'Cleanup rules',
        normalizeSpaces: 'Normalize spaces',
        normalizeSpacesDescription: 'Collapse regular ASCII spaces; preserve non-breaking spaces',
        removeEmptyLines: 'Remove blank lines',
        removeEmptyLinesDescription: 'Remove blank lines while keeping paragraphs on separate lines',
        joinWrappedLines: 'Join wrapped lines',
        joinWrappedLinesDescription: 'Join lines inside a paragraph without merging paragraphs',
        tabsToSpaces: 'Tabs → spaces',
        tabsToSpacesDescription: 'Replace each tab with two regular spaces',
        trimLines: 'Trim lines',
        trimLinesDescription: 'Remove spaces and tabs from each line edge',
        sourceText: 'Source text',
        resultText: 'Cleaned text',
        inputPlaceholder: 'Paste text copied from a PDF, OCR or email...',
        outputPlaceholder: 'The result will appear here',
        clear: 'Clear',
        copy: 'Copy',
        copied: 'Copied',
        copyError: 'Copy failed',
        characters: 'characters',
        lines: 'lines',
        emptyLines: 'blank lines',
        diffTitle: 'Change preview',
        before: 'Before',
        after: 'After',
        changed: 'Changed',
        unchanged: 'No changes',
        previewClipped: 'The preview is limited to the first 2,000 characters; the full result is available above.',
        limit: 'Limit: {characters} characters and {lines} lines.',
        characterLimitError: 'The text exceeds the character limit. Shorten it and try again.',
        lineLimitError: 'The text exceeds the line limit. Split it into several parts.',
        policyTitle: 'Predictable normalization',
        policyText:
          'Unicode and punctuation are preserved without NFC/NFKC normalization. Non-breaking spaces are unchanged. Output uses the dominant source line ending: LF, CRLF or CR; LF wins a tie.',
      },
    },
    transliteration: {
      title: 'Transliteration',
      shortDescription: 'Names in Latin script, GOST, URL slugs and reverse transliteration.',
      longDescription:
        'Local Russian transliteration using the ICAO document table, simplified GOST rules, URL slug rules or a best-effort reverse mode.',
      seoTitle: 'Russian transliteration online — ICAO, GOST and slug — txtify.ru',
      seoDescription:
        'Transliterate Russian text using ICAO or GOST, create a URL slug or run best-effort reverse transliteration. Local processing with no server upload.',
      useCases: ['Names in Latin script', 'GOST', 'URL slugs', 'Reverse transliteration'],
      howTo: [
        'Choose passport/ICAO, GOST, URL slug or reverse transliteration.',
        'Enter text and review the result that updates immediately in your browser.',
        'Copy the result and verify official spelling against the document requirements.',
      ],
      faq: [
        {
          question: 'Can I use this result for a passport?',
          answer:
            'ICAO mode is a reference conversion based on the Doc 9303 table. For an application or travel, use the spelling in your issued document or the issuing authority requirements.',
        },
        {
          question: 'How are ICAO and GOST different?',
          answer:
            'ICAO targets machine-readable travel documents and uses uppercase Latin output. GOST R 7.0.34-2014 defines simplified Russian transliteration and preserves letter case.',
        },
        {
          question: 'Is reverse transliteration exact?',
          answer:
            'No. Several Russian letters can produce the same Latin sequence, so reverse mode can only return a likely interpretation.',
        },
        {
          question: 'Is the text sent to a server?',
          answer:
            'No. All four modes run locally in your browser; source text and results are not uploaded or included in analytics.',
        },
      ],
    },
    fancyText: {
      title: 'Unicode styled text: Latin changes, Cyrillic stays unchanged',
      shortDescription: 'Eight Unicode styles for Basic Latin and some digits, with the exact source retained.',
      longDescription:
        'Local conversion of Basic Latin into eight Unicode character sets. Cyrillic, emoji and unsupported graphemes stay unchanged.',
      seoTitle: 'Unicode styled text online — 8 styles — txtify.ru',
      seoDescription:
        'Convert Latin letters and some digits into 8 Unicode character sets. Cyrillic stays unchanged. Local processing with no text upload.',
      useCases: ['Profiles and captions', 'Display names', 'Short emphasis', 'Exact plain-text fallback'],
      howTo: [
        'Enter text: Basic Latin is converted while Cyrillic and unsupported graphemes stay unchanged.',
        'Review all eight named variants and their coverage notes.',
        'Copy one variant or the exact original, then verify it after pasting.',
      ],
      faq: [
        {
          question: 'Does this create a new font?',
          answer:
            'No. It replaces some Latin letters and digits with individual Unicode characters. It does not create or download a font file.',
        },
        {
          question: 'Why does Cyrillic stay unchanged?',
          answer:
            'The selected mathematical Unicode sets have no direct Russian Cyrillic mappings. The tool preserves Cyrillic instead of substituting Latin or Greek lookalikes.',
        },
        {
          question: 'Will the result look identical everywhere?',
          answer:
            'Not necessarily. Rendering, filtering and counting vary by device and destination, so always verify the final text after pasting.',
        },
        {
          question: 'Is text sent to a server?',
          answer:
            'No. Conversion and copying happen locally; entered and copied text is not uploaded or stored.',
        },
      ],
    },
    seoLimits: {
      title: 'Snippet and SEO limit checker',
      shortDescription: 'Length of title, description, snippet and teaser text.',
      longDescription:
        'Local length checks for SEO titles, descriptions, snippets and teasers before publishing. Ranges are neutral editorial guidelines, not display guarantees for a specific platform.',
      seoTitle: 'Check snippet, title and description online — txtify.ru',
      seoDescription:
        'Check your snippet before publishing: SEO title, description and teaser length. Free, no sign-up and no server upload.',
      useCases: ['SEO title', 'Description', 'Snippet', 'Teaser text'],
      howTo: [
        'Paste an SEO title, description or snippet text into the matching field.',
        'Check the character count and status: short, in range or above the guideline.',
        'Trim or expand the copy, then move the final version back to your CMS.',
      ],
      faq: [
        {
          question: 'Are these exact search engine limits?',
          answer:
            'No. The tool shows neutral editorial ranges. Real snippet display depends on device, query, language and each platform decision.',
        },
        {
          question: 'Is the text sent to a server?',
          answer:
            'No. Counting happens locally in your browser, so text is not sent or stored.',
        },
        {
          question: 'Why count characters instead of pixels?',
          answer:
            'Character ranges are faster to use in an editorial workflow. Pixel width depends on font, interface and the exact display surface.',
        },
      ],
    },
    passwordGenerator: {
      title: 'Password generator',
      shortDescription: 'Cryptographically strong passwords with length, letters, numbers and symbols.',
      longDescription: 'A fast password generator for accounts, temporary access and test secrets. The password is created locally in your browser with crypto.getRandomValues, is never sent to a server and is not stored.',
      seoTitle: 'Password generator online — txtify.ru',
      seoDescription: 'Create a strong password locally in your browser: length, uppercase and lowercase letters, numbers, symbols and ambiguous character exclusion.',
      useCases: ['New account', 'Temporary access', 'Test secret', 'Admin panel'],
      howTo: [
        'Choose password length with the slider or number field.',
        'Keep the character sets you need: letters, numbers and symbols.',
        'Optionally exclude ambiguous characters.',
        'Settings refresh the password automatically; click New password for another value with the same settings.',
      ],
      faq: [
        {
          question: 'Is the password sent to a server?',
          answer: 'No. Generation happens locally in your browser with crypto.getRandomValues. The password is not sent, stored or included in analytics.',
        },
        {
          question: 'Can I turn off every character set?',
          answer: 'No. At least one set must stay enabled: lowercase letters, uppercase letters, numbers or symbols.',
        },
        {
          question: 'What does excluding ambiguous characters do?',
          answer: 'The tool removes characters such as 0, O, 1, I and l so the password is easier to type manually without mistakes.',
        },
      ],
    },
  },

  counter: {
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    placeholder: 'Paste text or start typing...',
    textareaLabel: 'Text for character counting',
    details: 'Details',
    chars: 'Characters',
    words: 'Words',
    withoutSpaces: 'Without spaces',
    lines: 'Lines',
    paragraphs: 'Paragraphs',
    sentences: 'Sentences',
    readingMinutes: 'Reading, min',
    utf8Bytes: 'UTF-8 bytes',
    quickLimits: 'Quick limits',
    charsShort: 'chars',
    limits: [
      ['SEO title', 60],
      ['Description', 160],
      ['Post 280', 280],
    ],
    howCountedTitle: 'How values are counted',
    howCounted:
      'Characters are counted as visible graphemes, so emoji and combined symbols are not split into multiple units. The no-spaces counter ignores spaces, tabs and line breaks.',
    usefulTitle: 'When this helps',
    useful:
      'The counter works well for forms with limits, SEO titles and descriptions, social posts, messages, technical text and checking UTF-8 string size.',
  },

  caseConverter: {
    sourceText: 'Source text',
    result: 'Result',
    clear: 'Clear',
    copy: 'Copy',
    copied: 'Copied',
    swap: 'Swap',
    inputPlaceholder: 'Paste text...',
    inputLabel: 'Source text for case conversion',
    outputPlaceholder: 'Conversion result',
    charsShort: 'chars',
    modeTitle: 'Conversion mode',
    statsTitle: 'Statistics',
    statsInput: 'Input',
    statsOutput: 'Result',
    statsWords: 'Words in input',
    statsMode: 'Mode',
  },

  transliteration: {
    modeTitle: 'Transliteration mode',
    modes: {
      icao: {
        label: 'Passport / ICAO',
        description: 'Uppercase Latin output based on the ICAO Doc 9303 table.',
      },
      gost: {
        label: 'GOST',
        description: 'Simplified transliteration from GOST R 7.0.34-2014.',
      },
      slug: {
        label: 'URL slug',
        description: 'Lowercase letters, digits and hyphens for a page address.',
      },
      reverse: {
        label: 'Reverse — best effort',
        description: 'Latin script to Cyrillic with unavoidable ambiguities.',
      },
    },
    russianInput: 'Russian text',
    latinInput: 'Latin-script text',
    inputPlaceholder: 'Example: Юлия Щербакова',
    reversePlaceholder: 'Example: IULIIA SHCHERBAKOVA',
    result: 'Result',
    outputPlaceholder: 'The transliteration will appear here',
    ready: 'The transliteration result was updated.',
    reset: 'Reset',
    copy: 'Copy',
    copied: 'Copied',
    copyError: 'Copy failed',
    characterLimitError: 'The text exceeds 100,000 characters. Split it into smaller parts.',
    officialTitle: 'Verify official spelling',
    officialText:
      'This result is a reference transformation, not an acceptance guarantee. For passports, visas, tickets or applications, use the spelling in the issued document and the receiving organization requirements.',
    sourcesTitle: 'Standards and local processing',
    localText:
      'Mappings were checked against ICAO Doc 9303 Part 3 and GOST R 7.0.34-2014. Input, results and clipboard contents stay in your browser.',
  },

  fancyText: {
    inputLabel: 'Text to convert into Unicode characters',
    instructions: 'Basic Latin A–Z and a–z are styled; digits change in four styles. Cyrillic and all other characters stay unchanged.',
    placeholder: 'Example: Hello Привет 123 😀',
    clear: 'Clear',
    copy: 'Copy',
    copyOriginal: 'Copy original text',
    originalStyleName: 'original text',
    copyStyle: 'Copy {style} Unicode text',
    copySuccess: 'Copied “{style}”. Verify the result after pasting.',
    copyError: 'Could not copy “{style}”. Allow clipboard access and try again.',
    resultsTitle: 'Eight Unicode variants',
    emptyResult: 'Enter text to preview this variant.',
    partialResult: 'Unsupported graphemes were preserved unchanged.',
    unchangedResult: 'This text has no characters supported by this style.',
    coverageTitle: 'Honest coverage',
    coverageText: 'All eight styles change Basic Latin. Digits change only in bold, double-struck, sans-serif bold and monospace. Cyrillic, accented Latin, emoji and other unsupported graphemes are preserved whole.',
    privacy: 'Conversion happens in this browser. Entered and copied text is not sent to a server.',
    compatibility: 'Unicode characters can render, filter and count differently. Verify the result after pasting; this tool does not guarantee acceptance by a destination.',
    accessibility: 'Styled output can be harder for screen readers than ordinary text. The exact original is always available nearby.',
    safetyWarning: 'The input contains hidden format, bidi, tag or control characters. The tool preserves them but does not generate new ones; verify the source before copying. Normal emoji ZWJ, variation-selector and modifier sequences do not trigger this warning.',
    graphemes: 'Graphemes',
    codePoints: 'Code points',
    utf16Units: 'UTF-16 units',
    checkAfterPaste: 'check after pasting',
    graphemeLimitError: 'The limit is 5,000 graphemes. The last accepted text is preserved.',
    codePointLimitError: 'The limit is 20,000 code points. The last accepted text is preserved.',
    styles: {
      bold: 'Bold',
      italic: 'Italic',
      'bold-italic': 'Bold italic',
      script: 'Script',
      fraktur: 'Fraktur',
      'double-struck': 'Double-struck',
      'sans-serif-bold': 'Sans-serif bold',
      monospace: 'Monospace',
    },
  },

  seoLimits: {
    recommended: 'Recommended',
    charactersShort: 'chars',
    summary: 'Summary',
    totalCharacters: 'Total characters',
    filledFields: 'Filled fields',
    goodFields: 'In range',
    noteTitle: 'How to read ranges',
    note:
      'Ranges help spot text that is too short or too long. They do not guarantee exact snippet display: final rendering depends on context and the platform interface.',
    rangeTitle: 'Guidelines',
    fields: {
      title: 'SEO title',
      description: 'Meta description',
      snippet: 'Snippet text',
      social: 'Teaser text',
    },
    placeholders: {
      title: 'Example: Character counter online — txtify.ru',
      description: 'Briefly describe the page so the content and action are clear.',
      snippet: 'Paste a text fragment that should work well as a snippet.',
      social: 'Short copy for a card, announcement or post.',
    },
    states: {
      empty: 'Empty',
      under: 'Short',
      good: 'In range',
      over: 'Long',
    },
  },

  passwordGenerator: {
    controls: 'Password settings',
    length: 'Length',
    lengthUnit: 'characters',
    characterSets: 'Character sets',
    uppercase: 'Uppercase letters',
    lowercase: 'Lowercase letters',
    numbers: 'Numbers',
    symbols: 'Symbols',
    excludeAmbiguous: 'Exclude ambiguous characters',
    excludeAmbiguousHint: 'Removes 0, O, 1, I and l for safer manual typing.',
    generate: 'New password',
    copy: 'Copy',
    copied: 'Copied',
    result: 'Password',
    resultPlaceholder: 'Creating a password with the selected settings…',
    securityNote: 'The password is created locally in your browser, is not sent to a server and is not stored.',
    emptySetError: 'Select at least one character set.',
    cryptoError: 'This browser did not provide a secure random generator.',
    entropy: 'Entropy',
    bits: 'bits',
    poolSize: 'Pool size',
    strength: {
      weak: 'Weak',
      fair: 'Fair',
      strong: 'Strong',
      excellent: 'Very strong',
    },
    stats: {
      selectedSets: 'Sets',
    },
  },

  blog: {
    breadcrumb: 'Blog',
    title: 'txtify blog',
    description:
      'Practical notes about text limits, line cleanup, formatting and local browser tools.',
    languageAria: 'Article language',
    postLanguageAria: 'Article language',
    empty: 'No posts in this language yet.',
    notFoundTitle: 'Post not found',
    notFoundDescription: 'This txtify blog post does not exist or the link is outdated.',
    relatedTools: 'Related tools',
    localeName: {
      ru: 'Русский',
      en: 'English',
    },
  },

  seo: {
    indexTitle: 'Free online text tools — txtify.ru',
    indexDescription:
      'Free local text tools for counting, cleanup, case conversion, transliteration, passwords and SEO limits with no server upload.',
    blogTitle: 'txtify blog - notes about text tools',
    blogDescription:
      'Practical notes about character counters, SEO limits, line cleanup, text formatting and local browser processing.',
  },
}
